import {
  isLedgerSyncConnectionStatus,
  type LedgerSyncConnectionStatus,
} from '@dcp/shared/ledgersync/connection-status'

/** A known status that needs the user (`requires_action`, `failed`, `disconnected`). */
export type AttentionStatus = Exclude<LedgerSyncConnectionStatus, 'active' | 'initiated'>

/** An attention status, or `'unknown'` for a value newer than this bundle. */
export type ConnectionAttention = AttentionStatus | 'unknown'

/**
 * Classify a raw connection status: does its data stop flowing until the user
 * reconnects? `active` (a live link) and `initiated` (a first connect that owns
 * no accounts yet) need nothing. Every other status does — including one newer
 * than this bundle, because hiding a warning is the unsafe direction.
 *
 * The one rule behind the connection list and detail header
 * (`presentConnectionStatus`), Credit Card Availability and the Balance Import
 * marker, so the surfaces cannot disagree.
 *
 * @param status - raw `ledgersync_connections.status`; null/undefined when there is no connection
 * @returns the attention status, `'unknown'` for an unrecognised value, or
 *          null when no attention is needed (or there is no connection)
 */
export function connectionAttention(status: string | null | undefined): ConnectionAttention | null {
  if (!status) return null
  if (!isLedgerSyncConnectionStatus(status)) return 'unknown'
  return status === 'active' || status === 'initiated' ? null : status
}

/**
 * Boolean form of {@link connectionAttention}.
 *
 * @param status - raw `ledgersync_connections.status`
 * @returns true for `requires_action`, `failed`, `disconnected` and any unknown value
 */
export function connectionNeedsAttention(status: string): boolean {
  return connectionAttention(status) !== null
}
